// Singleton that fetches the admin-uploaded default Melody Kit and exposes
// the loaded instrument samplers. Each slot (flute/piano/lead/brass/strings)
// becomes its own Tone.Sampler. When the AI lead asks to play a note, this
// manager picks the first non-empty instrument by priority order and triggers
// it — so admins can decide the timbre of the AI melody simply by uploading
// to the slot they want to use.
import * as Tone from "tone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Order in which instruments are auditioned when the melody trigger fires.
// Piano first because it is the most universally musical default; lead and
// the brass/strings/flute trio follow as alternates.
const PRIORITY = ["piano", "lead", "brass", "strings", "flute"];
// Single-sample slots are interpreted as rooted at this pitch — the
// Tone.Sampler pitch-shifts up/down from here for any requested note.
const ROOT_PITCH = "C4";

class MelodyKitManager {
  constructor() {
    this.kit = null;
    this.samplers = {};   // slot -> Tone.Sampler
    this.loaded = false;
    this.subscribers = new Set();
  }

  async refresh(force = false) {
    if (this.loaded && !force) return this.kit;
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/melody-kits/default`);
      const data = await res.json();
      const kit = data?.kit || null;
      // Dispose old samplers
      Object.values(this.samplers).forEach((s) => { try { s.dispose(); } catch { /* noop */ } });
      this.samplers = {};
      this.kit = kit;

      const samples = kit?.samples || {};
      const slotsLoaded = [];
      for (const [slot, meta] of Object.entries(samples)) {
        if (!meta?.url) continue;
        try {
          await Tone.start();
          const sampler = new Tone.Sampler({ urls: { [ROOT_PITCH]: `${BACKEND_URL}${meta.url}` } }).toDestination();
          this.samplers[slot] = sampler;
          slotsLoaded.push(slot);
        } catch (e) {
          console.warn(`MelodyKit: failed to load ${slot}`, e);
        }
      }
      if (slotsLoaded.length) await Tone.loaded();
      this.loaded = true;
      this.subscribers.forEach((fn) => { try { fn(this.kit); } catch { /* noop */ } });
      return this.kit;
    } catch (e) {
      console.warn("MelodyKit: refresh failed", e);
      return null;
    }
  }

  // Returns the highest-priority loaded sampler, or null when none is ready.
  _pickActive() {
    for (const slot of PRIORITY) {
      const s = this.samplers[slot];
      if (s && s.loaded !== false) return s;
    }
    return null;
  }

  isReady() { return !!this._pickActive(); }

  // Returns the priority-picked instrument slot + absolute URL for the
  // Melody Kit — the exporter needs both so it can rebuild a fresh
  // Tone.Sampler at the same root pitch inside the Tone.Offline context.
  // Returns { slot, url, rootPitch } or null when no slot is loaded.
  getActiveSampleUrl() {
    const samples = this.kit?.samples || {};
    for (const slot of PRIORITY) {
      const meta = samples[slot];
      if (meta?.url) return { slot, url: `${BACKEND_URL}${meta.url}`, rootPitch: ROOT_PITCH };
    }
    return null;
  }

  // Returns { slot: url } for EVERY uploaded melody slot (not just the priority
  // pick). Used by the DAW's Sampler-track "Kit Sound Library" dropdown so the
  // producer can pick any individual melodic sound (piano / lead / brass /
  // strings / flute) rather than being locked to the priority order.
  getAllSampleUrls() {
    const out = {};
    const samples = this.kit?.samples || {};
    for (const slot of PRIORITY) {
      const meta = samples[slot];
      if (meta?.url) out[slot] = `${BACKEND_URL}${meta.url}`;
    }
    return out;
  }

  // Convenience for the sampler UI: the pitch at which all melody kit
  // single-sample slots are considered "rooted".
  getRootPitch() { return ROOT_PITCH; }

  trigger(pitch, time, durationSec = 0.5, velocity = 1) {
    const s = this._pickActive();
    if (!s) return false;
    try {
      s.triggerAttackRelease(pitch, durationSec, time, velocity);
      return true;
    } catch {
      return false;
    }
  }

  getKit() { return this.kit; }
  subscribe(fn) { this.subscribers.add(fn); return () => this.subscribers.delete(fn); }
}

const melodyKitManager = new MelodyKitManager();
export default melodyKitManager;
